import {FC, useMemo} from 'react'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {
  getMishapInterval,
  isOfflineMishap,
  isStatusMishap,
} from '../mappers/fromMishaps'
import {MishapBlockedJobsCorrelation, PrintStoryDoc, TimeWindowMs} from '../types'
import {toMillis} from '../time'
import {jobStatusBarColor} from '../severity'

type Props = {
  mishaps: PrintStoryDoc[]
  printJobs?: PrintStoryDoc[]
  window: TimeWindowMs
  correlations?: MishapBlockedJobsCorrelation[]
}

export const PrinterIncidentGantt: FC<Props> = ({
  mishaps,
  printJobs = [],
  window,
  correlations = [],
}) => {
  const {series, options, hasData} = useMemo(() => {
    const offline: any[] = []
    const status: any[] = []
    const jobs: any[] = []

    mishaps.forEach((mishap) => {
      const interval = getMishapInterval(mishap)
      if (!interval) {
        return
      }
      const start = Math.max(interval.startMs, window.startMs)
      const end = Math.min(interval.endMs, window.endMs)
      if (end <= start) {
        return
      }

      const correlation = correlations.find((c) => c.mishapId === mishap.id)
      const blockedNote =
        correlation && correlation.blockedJobCount > 0
          ? ` · ${correlation.blockedJobCount} blocked print job(s)`
          : ''

      const item = {
        x: isOfflineMishap(mishap) ? 'Heartbeat missing' : 'Status issues',
        y: [start, end],
        fillColor: isOfflineMishap(mishap)
          ? interval.resolved
            ? '#f1416c'
            : '#b02a37'
          : String(mishap.severity) === 'attention'
          ? '#ffc700'
          : '#f1416c',
        meta: {
          title: mishap.type || 'mishap',
          detail: `${mishap.reason || mishap.status || mishap.staffMessage || ''}${blockedNote}`,
          ongoing: !interval.resolved,
        },
      }

      if (isOfflineMishap(mishap)) {
        offline.push(item)
      } else if (isStatusMishap(mishap)) {
        status.push(item)
      }
    })

    printJobs.forEach((job) => {
      const ts =
        toMillis(job.publishedAtMs) ||
        toMillis(job.timestamps?.createdAt) ||
        toMillis(job.createdAtMs) ||
        toMillis(job.createdAt)
      if (!ts || ts < window.startMs || ts > window.endMs) {
        return
      }
      const status = String(job.status || 'pending').toLowerCase()
      jobs.push({
        x: 'Print jobs',
        y: [ts, ts + 60_000],
        fillColor: jobStatusBarColor(status),
        meta: {
          title: job.jobId || job.id,
          detail: status,
          ongoing: false,
        },
      })
    })

    const seriesData = [
      {name: 'Heartbeat missing', data: offline},
      {name: 'Status issues', data: status},
      {name: 'Print jobs', data: jobs},
    ].filter((s) => s.data.length > 0)

    const chartOptions: ApexOptions = {
      chart: {
        type: 'rangeBar',
        toolbar: {show: false},
        animations: {enabled: false},
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '60%',
          rangeBarGroupRows: true,
        },
      },
      xaxis: {
        type: 'datetime',
        min: window.startMs,
        max: window.endMs,
      },
      yaxis: {
        labels: {
          style: {fontSize: '12px'},
        },
      },
      tooltip: {
        custom: ({seriesIndex, dataPointIndex, w}) => {
          const point = w.config.series[seriesIndex]?.data?.[dataPointIndex]
          const meta = point?.meta || {}
          return `<div class="p-3"><div class="fw-bold">${meta.title || ''}</div><div class="text-muted">${
            meta.detail || ''
          }</div>${meta.ongoing ? '<div class="text-danger">Ongoing</div>' : ''}</div>`
        },
      },
      legend: {show: true},
      grid: {borderColor: '#eff2f5'},
    }

    return {
      series: seriesData,
      options: chartOptions,
      hasData: seriesData.length > 0,
    }
  }, [mishaps, printJobs, window, correlations])

  if (!hasData) {
    return (
      <div className='border border-dashed rounded p-5 mb-6'>
        <div className='fw-bold text-gray-800 mb-1'>Incident timeline</div>
        <div className='text-muted'>No mishaps or print jobs in the selected window.</div>
      </div>
    )
  }

  return (
    <div className='border border-dashed rounded p-5 mb-6'>
      <div className='fw-bold text-gray-800 fs-5 mb-1'>Incident timeline</div>
      <div className='text-muted fs-7 mb-4'>
        Real time-scale swimlanes from printerMishaps. Print jobs appear as thin marks.
      </div>
      <Chart options={options} series={series} type='rangeBar' height={Math.max(220, series.length * 90)} />
    </div>
  )
}
